/**
 * 字牌（跑胡子）核心类型定义
 */

// 牌的类型：大字或小字
export type CardType = 'big' | 'small';

// 牌的数值 (1-10)
export type CardValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

// 单张牌接口
export interface Card {
  id: string; // 唯一标识
  type: CardType; // 大字或小字
  value: CardValue; // 1-10
  isBig: boolean; // 是否为大字 (壹-拾)
  isRed: boolean; // 是否为红牌（二、七、十）
  name: string; // 显示名称 (如: "壹", "一", "柒", "七")
  pinyin: string; // 拼音标识
}

// 牌组/面子类型
export type MeldType =
  | 'TI' // 提 (起手4张或摸到第4张，12/9胡)
  | 'PAO' // 跑 (碰/扫后碰见第4张，或手中有坎别人打/摸第4张，12/9胡)
  | 'SAO' // 扫/啸 (手中有2张，自己摸到第3张，明牌在桌但为暗坎，6/3胡)
  | 'KAN' // 坎/暗坎 (起手手中3张相同牌未亮出，6/3胡)
  | 'PENG' // 碰 (手中有2张，别人打出或摸出第3张，3/1胡)
  | 'CHI_123' // 壹贰叁/一二三顺 (6/3胡)
  | 'CHI_2710' // 贰柒拾/二七十顺 (6/3胡)
  | 'CHI_SEQ' // 普通顺子 (如 4-5-6，0胡)
  | 'CHI_JIAO' // 绞牌/大小字配 (如 壹壹一 或 壹一一，0胡)
  | 'PAIR'; // 对子/将牌 (0胡)

// 牌组/面子数据结构
export interface Meld {
  id: string;
  type: MeldType;
  cards: Card[];
  huxi: number; // 该组提供的胡息
  isExposed: boolean; // 是否在桌面上亮出
  sourcePlayerId?: string; // 来源玩家
}

// 玩家位置
export type PlayerPosition = 'bottom' | 'right' | 'left';

// 玩家接口
export interface Player {
  id: string;
  name: string;
  avatar: string;
  position: PlayerPosition;
  isDealer: boolean; // 是否为庄家
  hand: Card[]; // 手牌
  handColumns: Card[][]; // 手牌按列/柱组织的结构 (经典字牌手牌理牌)
  melds: Meld[]; // 桌面亮牌区 (吃、碰、提、跑、扫)
  discards: Card[]; // 个人弃牌区 (自己打出的牌)
  score: number; // 累计得分
  roundHuxi: number; // 当前局实时胡息 (已亮出面子 + 手牌暗坎/提)
  isBot: boolean;
  botDifficulty?: BotDifficulty;
}

// 游戏状态枚举
export enum GameState {
  IDLE = 'IDLE', // 未开始
  DEALING = 'DEALING', // 发牌与理牌动画
  WAITING_DRAW = 'WAITING_DRAW', // 等待当前回合玩家摸牌
  WAITING_DISCARD = 'WAITING_DISCARD', // 等待当前回合玩家出牌
  CARD_DRAWN = 'CARD_DRAWN', // 摸牌进判定区
  WAITING_ACTIONS = 'WAITING_ACTIONS', // 等待各玩家选择响应 (胡/跑/提/碰/吃/过)
  ROUND_OVER = 'ROUND_OVER', // 单局结算
  MATCH_OVER = 'MATCH_OVER' // 整场比赛结算
}

// 玩家操作动作
export enum ActionType {
  DRAW = 'DRAW', // 摸牌
  DISCARD = 'DISCARD', // 出牌
  TI = 'TI', // 提
  PAO = 'PAO', // 跑
  SAO = 'SAO', // 扫
  PENG = 'PENG', // 碰
  CHI = 'CHI', // 吃
  HU = 'HU', // 胡牌
  PASS = 'PASS' // 过 / 放弃
}

// 吃牌方案选项
export interface ChiOption {
  id: string;
  type: MeldType;
  targetCard: Card; // 吃的目标牌 (判定区的牌)
  handCards: Card[]; // 从手牌中拿出的牌
  meldCards: Card[]; // 组合成的三张牌
  name: string; // 方案名称描述，如 "一二三 (3胡)" 或 "绞牌: 贰贰二"
  huxi: number; // 带来的胡息
  biCards?: {
    handCards: Card[];
    meldCards: Card[];
    type: MeldType;
    huxi: number;
  }[]; // 比牌需求 (若有)
}

// 玩家可用动作集合
export interface AvailableActions {
  canHu: boolean;
  canTi: boolean;
  canPao: boolean;
  canSao: boolean;
  canPeng: boolean;
  canChi: boolean;
  chiOptions: ChiOption[];
  canPass: boolean;
}

// 胡牌详细结算信息
export interface WinResult {
  isWin: boolean;
  winnerId: string;
  winCard: Card;
  isSelfDraw: boolean; // 是否自摸
  totalHuxi: number; // 总胡息
  melds: Meld[]; // 所有胡牌组合 (亮牌 + 手牌组合)
  eyes: Card[]; // 将牌 (对子)
  specialPatterns: string[]; // 名堂 (如: 红胡, 黑胡, 点胡, 天胡, 地胡, 碰碰胡等)
  scoreDelta: Record<string, number>; // 各玩家得分变动
}

// 机器人难度
export enum BotDifficulty {
  EASY = 'EASY', // 菜鸟 (随意出牌、无脑吃碰)
  MEDIUM = 'MEDIUM', // 老手 (懂得留坎、算胡息)
  HARD = 'HARD' // 雀圣 (精通防守、控牌、拆对保胡)
}

export type HandSize = 15 | 21;

// 游戏全局配置
export interface GameConfig {
  playerCount: number; // 默认3人
  handSize: HandSize; // 每位玩家总手牌数（含庄家起手摸牌）
  minHuxiToWin: number; // 起胡胡息 (默认15胡)
  rules?: import('./rules.config').RuleConfig;
  totalRounds: number; // 局数 (如4局或8局)
  botDifficulty: BotDifficulty;
  soundEnabled: boolean;
  gameSpeed: 'slow' | 'normal' | 'fast';
}

// 游戏事件
export interface GameEvent {
  type:
    | 'DEAL_CARDS'
    | 'CARD_DRAWN'
    | 'CARD_DISCARDED'
    | 'ACTION_PERFORMED'
    | 'ROUND_WIN'
    | 'ROUND_DRAW'
    | 'MESSAGE';
  payload: any;
}
